/**
 * Emergency SOS Service
 *
 * Captures the user's GPS location, writes an alert document to Firestore,
 * and triggers an FCM push notification (via Cloud Function) to all family
 * members and the user's saved emergency contacts.
 *
 * Firestore path: families/{familyCode}/emergencyAlerts/{alertId}
 * The Cloud Function `onEmergencyAlertCreated` listens to this path and
 * fans out FCM pushes to family members.
 */

import { db } from '../firebase';
import {
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import logger from '../utils/logger';

/**
 * Reverse-geocode a lat/lng to a human-readable address using the free
 * OSM Nominatim API (no API key required).
 * Returns a short address string, or null on failure.
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'en', 'User-Agent': 'FamilyForLife/1.0' } }
    );
    if (!resp.ok) return null;
    const data = await resp.json();
    // Build a short address: house number + road + city/town
    const a = data.address || {};
    const parts = [
      a.house_number,
      a.road || a.pedestrian || a.footway,
      a.neighbourhood || a.suburb,
      a.city || a.town || a.village || a.county,
      a.state,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : data.display_name || null;
  } catch {
    return null;
  }
};

/**
 * Capture the device's current GPS coordinates.
 * Returns { lat, lng } or null if unavailable/denied.
 */
export const captureGPS = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        logger.warn('GPS capture failed:', err.message);
        resolve(null);
      },
      { timeout: 8000, maximumAge: 0 }
    );
  });

/**
 * Send an Emergency SOS alert for the current user.
 *
 * @param {object} params
 * @param {string}   params.userId        - Sender's user ID
 * @param {string}   params.displayName   - Sender's display name
 * @param {string}   params.familyCode           - Family code
 * @param {string}   [params.message]            - Optional custom message
 * @param {string[]} [params.emergencyContactIds] - User IDs in the Emergency Contacts group
 * @param {Array}    [params.emergencyContacts]   - External contacts (name/phone, non-app users)
 *
 * @returns {Promise<{ alertId: string, location: object|null }>}
 */
export const sendEmergencySOS = async ({
  userId,
  displayName,
  familyCode,
  message = '',
  emergencyContactIds = [],
  emergencyContacts = [],
}) => {
  if (!userId || !familyCode) {
    throw new Error('userId and familyCode are required to send an SOS alert');
  }

  // Try to capture GPS (best-effort — alert still sends if GPS is unavailable)
  const location = await captureGPS();

  // Reverse-geocode to a human-readable address
  const locationAddress = location ? await reverseGeocode(location.lat, location.lng) : null;

  // Use address-based Maps URL matching LocationPicker.js buildMapsUrl() pattern
  // Falls back to coordinates when reverse geocoding fails
  const mapsLink = location
    ? locationAddress
      ? `https://www.google.com/maps/search/${encodeURIComponent(locationAddress)}`
      : `https://maps.google.com/?q=${location.lat},${location.lng}`
    : null;

  const alertData = {
    userId,
    displayName: displayName || 'A family member',
    familyCode,
    message: message || `${displayName || 'A family member'} needs immediate help!`,
    location: location || null,
    locationAddress: locationAddress || null,
    mapsLink,
    // App users in the sender's Emergency Contacts group — Cloud Function targets these for push
    emergencyContactIds,
    // External (non-app) contacts stored on the profile — name + phone only
    emergencyContacts,
    status: 'active', // 'active' | 'resolved'
    createdAt: serverTimestamp(),
    resolvedAt: null,
  };

  const alertRef = await addDoc(
    collection(db, 'families', familyCode, 'emergencyAlerts'),
    alertData
  );

  logger.info('Emergency SOS sent:', alertRef.id, location ? 'with GPS' : 'without GPS');

  return { alertId: alertRef.id, location };
};

/**
 * Resolve (cancel) an active SOS alert.
 */
export const resolveEmergencySOS = async (familyCode, alertId) => {
  const { doc, updateDoc } = await import('firebase/firestore');
  const alertRef = doc(db, 'families', familyCode, 'emergencyAlerts', alertId);
  await updateDoc(alertRef, {
    status: 'resolved',
    resolvedAt: serverTimestamp(),
  });
};
